import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import PhoneField, { PhoneFieldHandle } from "./PhoneField";
import VStack from "../ui_atoms/VStack";
import { ButtonProps } from "./Button";
import { SEND_INVITATION } from "../graphql/invitation";
import { useI18n } from "../hooks/i18n-hooks";
import Callout from "../ui_atoms/Callout";
import TextField from "./TextField";
import { GET_WORKER_INFO } from "../graphql/core";
import { AuthManagerContext } from "../contexts/AuthManager";
import { useTheme } from "../hooks/use-theme";
import { StyleSheet, Platform } from "react-native";
import { PhoneContactChooser } from "./phone_contact/PhoneContactChooser";
import { GET_CLIENT_AND_INVITATION_WORKERS } from "../graphql/client";
import Gap from "../ui_atoms/Gap";
import Text from "./Text";
import { useTracking } from "../hooks/tracking";
import Label from "../ui_atoms/Label";

type SendInvitationComponentProps = {
    onSuccess: () => void;
    setButtonProps: (buttonProps: Partial<ButtonProps>) => void;
};

const SendInvitationComponent: React.FC<SendInvitationComponentProps> = ({
    onSuccess,
    setButtonProps,
}) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { user } = useContext(AuthManagerContext);
    const [sendInvitation, { loading, error, reset }] = useMutation(SEND_INVITATION, {
        refetchQueries: [GET_CLIENT_AND_INVITATION_WORKERS],
    });
    const [phoneNumberError, setPhoneNumberError] = useState<string>();
    const [sendError, setSendError] = useState<string>();
    const [getWorkerInfo] = useLazyQuery(GET_WORKER_INFO);
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const phoneInputRef = useRef<PhoneFieldHandle>(null);
    const isPhoneValid = phoneInputRef.current?.isValidNumber(phoneNumber);
    const [canSumbit, setCanSubmit] = useState<boolean>(false);
    const { trackEvent } = useTracking();
    const clearErrors = useCallback(() => {
        setPhoneNumberError(undefined);
        setSendError(undefined);
        reset();
    }, [reset, setPhoneNumberError, setSendError]);

    const onSendInvitation = useCallback(async () => {
        if (isPhoneValid) {
            phoneInputRef.current?.formatPhoneNumber();
            const result = await sendInvitation({
                variables: { phoneNumber, firstName, lastName },
            });
            void trackEvent("click_send_invitation");
            if (result.data?.sendInvitation?.__typename === "SendInvitationSuccess") {
                onSuccess();
            } else if (result.data?.sendInvitation?.__typename === "SendInvitationError") {
                setSendError(
                    i18n.t(`invitation.send.errors.${result.data?.sendInvitation?.messageKey}`),
                );
            }
        }
    }, [
        isPhoneValid,
        sendInvitation,
        phoneNumber,
        onSuccess,
        firstName,
        lastName,
        i18n,
        trackEvent,
    ]);

    const errorMessage = useMemo(() => {
        if (error) {
            return i18n.t("errors.unknown_error");
        }
        if (sendError) {
            return sendError;
        }
        return undefined;
    }, [error, sendError, i18n]);

    useEffect(() => {
        const asyncCheck = async () => {
            const existingWorkerInfo = await getWorkerInfo({ variables: { phoneNumber } });
            if (user?.phoneNumber === phoneNumber) {
                setPhoneNumberError(i18n.t("invitation.send.errors.selfInviteError"));
                setCanSubmit(false);
                return;
            }
            if (existingWorkerInfo.data?.getWorkerInfo) {
                setPhoneNumberError(i18n.t("invitation.send.errors.workerExistsError"));
                setCanSubmit(false);
            } else {
                setPhoneNumberError(undefined);
                setCanSubmit(true);
            }
        };
        if (isPhoneValid) {
            void asyncCheck();
        } else {
            setCanSubmit(false);
            setPhoneNumberError(undefined);
        }
    }, [isPhoneValid, phoneNumber, getWorkerInfo, setPhoneNumberError, i18n, user]);

    useEffect(() => {
        setButtonProps({
            onPress: onSendInvitation,
            disabled: !canSumbit || loading,
            processing: loading,
        });
    }, [canSumbit, loading, onSendInvitation, setButtonProps]);

    return (
        <VStack gap={0} alignment="stretch" style={{ flex: 1 }}>
            {errorMessage && (
                <>
                    <Callout markdown={errorMessage} mode="error" onClose={clearErrors} />
                    <Gap gap={1} />
                </>
            )}
            <Text style={styles.text}>{i18n.t("invitation.details.description")}</Text>
            <Gap gap={1} />
            <TextField
                style={{ flexGrow: 0 }}
                label={i18n.t("invitation.send.firstName")}
                disabled={loading}
                value={firstName}
                onChangeText={setFirstName}
            />
            <Gap gap={1} />
            <TextField
                style={{ flexGrow: 0 }}
                label={i18n.t("invitation.send.lastName")}
                disabled={loading}
                value={lastName}
                onChangeText={setLastName}
            />
            <Gap gap={1} />
            <PhoneField
                style={{ flexGrow: 0 }}
                label={i18n.t("invitation.send.phoneNumber")}
                ref={phoneInputRef}
                onPhoneNumberChanged={setPhoneNumber}
                disabled={loading}
                error={phoneNumberError}
                value={phoneNumber}
            />
            {Platform.OS !== "web" && (
                <>
                    <Gap gap={1 / 2} />
                    <PhoneContactChooser
                        buttonMode="link"
                        buttonSize="small"
                        buttonLabel={i18n.t("invitation.send.my_contacts")}
                        onSelect={(c) => {
                            if (c) {
                                setPhoneNumber(c.phoneNumber);
                                setFirstName(c.firstName || "");
                                setLastName(c.lastName || "");
                            }
                        }}
                    />
                </>
            )}
            <Gap gap={1} />
            <Label mode="grey" text={i18n.t("invitation.send.explaination")} multiline />
        </VStack>
    );
};

export default SendInvitationComponent;

const useStyles = () => {
    const { colors, fonts } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                text: {
                    ...fonts.bodyMedium,
                    color: colors.text,
                },
            }),
        [colors.text, fonts.bodyMedium],
    );
};
